/**
 * `/terms` — terms of service, and the other half of the Google OAuth consent screen.
 *
 * Google shows this link beside the privacy policy when somebody is asked to grant access, so it
 * exists for the same reason `/privacy` does: an External OAuth app cannot leave "Testing" while
 * these are missing, and until it does, only listed test users can sign in at all.
 *
 * Deliberately short and honest about what this is — a free game in beta, run by one person. A
 * long enterprise EULA here would be less truthful, not more.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/lib/contact';

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});

/** Last substantive review of this text. */
const LAST_UPDATED = '29 August 2026';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

function TermsPage() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10">
      <h1 className="text-3xl font-black tracking-tight">Terms of Service</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Chompdome is in beta. Last updated {LAST_UPDATED}.
      </p>

      <Section title="What this is">
        <p>
          Chompdome is a free multiplayer browser and mobile game, currently in beta. It is provided
          as-is: expect changes, downtime, and the occasional reset while it is being built.
        </p>
      </Section>

      <Section title="Playing fairly">
        <p>
          Play the game as it is presented. Do not use modified clients, automation or exploits to
          gain an advantage, and do not attempt to disrupt the service for other players.
        </p>
        <p>
          Names and uploaded pictures are shown to everyone in the room. Keep them free of
          harassment, hate, sexual content and anything illegal.
        </p>
        <p>
          Accounts that break these rules can be suspended or banned. Bans state a reason and, where
          they are temporary, an expiry.
        </p>
      </Section>

      <Section title="Your content">
        <p>
          Pictures you upload stay yours. By uploading one you allow us to store it and display it
          to other players as your in-game appearance, for as long as you keep it on your account.
        </p>
      </Section>

      <Section title="Accounts">
        <p>
          You are responsible for what happens under your account. Guest accounts are tied to a
          device identifier and can be lost — sign in if you want your progress to persist.
        </p>
      </Section>

      <Section title="No warranty">
        <p>
          The game is provided without warranty of any kind. It may be unavailable, and beta data
          may be lost. Do not rely on it for anything that matters.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions go to{' '}
          <a href={SUPPORT_MAILTO} className="font-medium text-primary hover:underline">
            {SUPPORT_EMAIL}
          </a>
          . See also our{' '}
          <Link to="/privacy" className="font-medium text-primary hover:underline">
            privacy policy
          </Link>
          .
        </p>
      </Section>

      <p className="mt-6 text-center text-sm">
        <Link to="/" className="text-muted-foreground hover:underline">
          Back to Chompdome
        </Link>
      </p>
    </div>
  );
}
